import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    TextField,
    InputAdornment,
    Chip,
    Paper,
    CircularProgress,
    Alert,
    Divider,
    useTheme,
    alpha,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { faqService, FaqArticle, FaqCategory, FaqGrouped } from '../../services/faqService';

const FaqPage: React.FC = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [grouped, setGrouped] = useState<FaqGrouped[]>([]);
    const [categories, setCategories] = useState<FaqCategory[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedArticle, setExpandedArticle] = useState<number | false>(false);

    useEffect(() => {
        loadFaq();
    }, [selectedCategory]);

    useEffect(() => {
        const debounce = setTimeout(() => {
            if (searchQuery.length >= 2 || searchQuery.length === 0) {
                loadFaq();
            }
        }, 300);
        return () => clearTimeout(debounce);
    }, [searchQuery]);

    const loadFaq = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await faqService.getFaqArticles(
                selectedCategory || undefined,
                searchQuery || undefined
            );
            // Defensiv: wenn das Backend keine public FAQ-Categories hat, fehlt `grouped`
            // im Early-Return-Pfad (siehe PublicWikiController). Nicht auf undefined setzen.
            setGrouped(data.grouped ?? []);
            setCategories(data.categories ?? []);
        } catch (err: any) {
            setError(err.message || 'Fehler beim Laden der FAQ');
        } finally {
            setLoading(false);
        }
    };

    const handleCategoryClick = (categoryId: number | null) => {
        setSelectedCategory(categoryId);
        setExpandedArticle(false);
    };

    const handleAccordionChange = (articleId: number) => (
        _event: React.SyntheticEvent,
        isExpanded: boolean
    ) => {
        setExpandedArticle(isExpanded ? articleId : false);
    };

    const totalArticles = grouped.reduce((sum, g) => sum + g.articles.length, 0);

    return (
        <Box
            sx={{
                minHeight: '100vh',
                bgcolor: 'grey.50',
                py: 4,
            }}
        >
            <Container maxWidth="md">
                {/* Header */}
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <HelpOutlineIcon
                        sx={{
                            fontSize: 64,
                            color: 'primary.main',
                            mb: 2,
                        }}
                    />
                    <Typography variant="h3" component="h1" gutterBottom fontWeight={700}>
                        Hilfe & FAQ
                    </Typography>
                    <Typography variant="h6" color="text.secondary">
                        Finden Sie Antworten auf haeufig gestellte Fragen
                    </Typography>
                </Box>

                {/* Search */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 2,
                        mb: 3,
                        bgcolor: 'white',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'grey.200',
                    }}
                >
                    <TextField
                        fullWidth
                        placeholder="FAQ durchsuchen..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                            },
                        }}
                    />
                </Paper>

                {/* Category Filter */}
                {categories.length > 0 && (
                    <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        <Chip
                            label="Alle"
                            onClick={() => handleCategoryClick(null)}
                            variant={selectedCategory === null ? 'filled' : 'outlined'}
                            color={selectedCategory === null ? 'primary' : 'default'}
                        />
                        {categories.map((cat) => (
                            <Chip
                                key={cat.id}
                                label={cat.name}
                                onClick={() => handleCategoryClick(cat.id)}
                                variant={selectedCategory === cat.id ? 'filled' : 'outlined'}
                                color={selectedCategory === cat.id ? 'primary' : 'default'}
                                sx={{
                                    borderColor: cat.color,
                                    ...(selectedCategory === cat.id && {
                                        bgcolor: cat.color,
                                        color: 'white',
                                        '&:hover': {
                                            bgcolor: cat.color,
                                        },
                                    }),
                                }}
                            />
                        ))}
                    </Box>
                )}

                {/* Loading */}
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress />
                    </Box>
                )}

                {/* Error */}
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                {/* No Results */}
                {!loading && !error && totalArticles === 0 && (
                    <Paper
                        sx={{
                            p: 4,
                            textAlign: 'center',
                            bgcolor: 'white',
                            borderRadius: 2,
                        }}
                    >
                        <Typography color="text.secondary">
                            {searchQuery
                                ? 'Keine FAQ-Eintraege zu Ihrer Suche gefunden.'
                                : 'Noch keine FAQ-Eintraege vorhanden.'}
                        </Typography>
                    </Paper>
                )}

                {/* FAQ Accordion Groups */}
                {!loading && !error && grouped.map((group, groupIndex) => (
                    <Box key={group.category?.id || 'uncategorized'} sx={{ mb: 4 }}>
                        {/* Category Header */}
                        {group.category && (
                            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box
                                    sx={{
                                        width: 4,
                                        height: 24,
                                        bgcolor: group.category.color,
                                        borderRadius: 1,
                                    }}
                                />
                                <Typography variant="h6" fontWeight={600}>
                                    {group.category.name}
                                </Typography>
                                <Chip
                                    label={group.articles.length}
                                    size="small"
                                    sx={{ ml: 1 }}
                                />
                            </Box>
                        )}

                        {/* Articles */}
                        <Paper
                            elevation={0}
                            sx={{
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'grey.200',
                                overflow: 'hidden',
                            }}
                        >
                            {group.articles.map((article, index) => (
                                <Accordion
                                    key={article.id}
                                    expanded={expandedArticle === article.id}
                                    onChange={handleAccordionChange(article.id)}
                                    disableGutters
                                    elevation={0}
                                    sx={{
                                        '&:before': { display: 'none' },
                                        borderBottom: index < group.articles.length - 1 ? '1px solid' : 'none',
                                        borderColor: 'grey.200',
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        sx={{
                                            bgcolor: expandedArticle === article.id
                                                ? alpha(theme.palette.primary.main, 0.05)
                                                : 'white',
                                            '&:hover': {
                                                bgcolor: alpha(theme.palette.primary.main, 0.05),
                                            },
                                            py: 1,
                                        }}
                                    >
                                        <Typography fontWeight={500}>
                                            {article.title}
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails
                                        sx={{
                                            bgcolor: 'grey.50',
                                            borderTop: '1px solid',
                                            borderColor: 'grey.200',
                                        }}
                                    >
                                        {/* Summary as intro if available */}
                                        {article.summary && (
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ mb: 2, fontStyle: 'italic' }}
                                            >
                                                {article.summary}
                                            </Typography>
                                        )}

                                        {/* Content - will be loaded on expand */}
                                        <FaqArticleContent slug={article.slug} />
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                        </Paper>
                    </Box>
                ))}

                {/* Footer */}
                <Box sx={{ textAlign: 'center', mt: 6, color: 'text.secondary' }}>
                    <Divider sx={{ mb: 3 }} />
                    <Typography variant="body2">
                        Ihre Frage nicht gefunden? Kontaktieren Sie unseren Support.
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

// Separate component to load full article content
const FaqArticleContent: React.FC<{ slug: string }> = ({ slug }) => {
    const [content, setContent] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        const loadContent = async () => {
            try {
                const article = await faqService.getArticle(slug);
                setContent(article.content || 'Kein Inhalt verfuegbar.');
            } catch {
                setError(true);
            } finally {
                setLoading(false);
            }
        };
        loadContent();
    }, [slug]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    if (error) {
        return (
            <Typography color="error" variant="body2">
                Inhalt konnte nicht geladen werden.
            </Typography>
        );
    }

    return (
        <Box
            sx={{
                '& p': { mb: 2 },
                '& ul, & ol': { pl: 3, mb: 2 },
                '& a': { color: 'primary.main' },
            }}
            dangerouslySetInnerHTML={{ __html: content || '' }}
        />
    );
};

export default FaqPage;
